function asyncGeneratorStep(gen, resolve, reject, _next, _throw, key, arg) {
    try {
        var info = gen[key](arg);
        var value = info.value;
    } catch (error) {
        reject(error);
        return;
    }
    if (info.done) {
        resolve(value);
    } else {
        Promise.resolve(value).then(_next, _throw);
    }
}
function _async_to_generator(fn) {
    return function() {
        var self = this, args = arguments;
        return new Promise(function(resolve, reject) {
            var gen = fn.apply(self, args);
            function _next(value) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "next", value);
            }
            function _throw(err) {
                asyncGeneratorStep(gen, resolve, reject, _next, _throw, "throw", err);
            }
            _next(undefined);
        });
    };
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
import { Cookies } from '@stripe-internal/stripe-cookies';
// we have to use direct imports to avoid the react dependency
import { AnalyticsReporter } from '@stripe-internal/analytics-js/dist/src/core/AnalyticsReporter';
import { captureWebVitals } from '../webVitals';
import { isProduction } from '../environment';
import { createURL, getCleanedId, getHostAndPath } from '../util';
const cookies = new Cookies();
// Desired UTM Parameters to track
const UTM_PARAMS = {
    gclid: true,
    msclkid: true,
    fbclid: true,
    trk: true,
    gspk: true,
    gsxid: true,
    utm_campaign: true,
    utm_source: true,
    utm_term: true,
    utm_medium: true,
    utm_content: true,
    utm_matchtype: true,
    utm_adposition: true,
    utm_device: true
};
// Parameters used by the experimentation infrastructure
// anything with dimensions- prefix can be used as a dimension in Kepler to create segments from
const EXPERIMENT_DIMENSIONS = {
    'dimensions-viewport_width': true,
    'dimensions-viewport_height': true,
    'dimensions-logged_in': true,
    'dimensions-has_logged_in_last_six_months': true
};
const EXPERIMENTATION_PARAMS = _object_spread({
    arb_id: true,
    experiment_retrieved: true,
    token_b057adf6: true
}, EXPERIMENT_DIMENSIONS);
const UA_PARAMS = {
    ua_browser: true,
    ua_browser_name: true,
    ua_browser_version: true,
    ua_device_model: true,
    ua_device_type: true,
    ua_device_vendor: true,
    ua_engine_name: true,
    ua_engine_version: true,
    ua_mobile: true,
    ua_model: true,
    ua_os_name: true,
    ua_os_version: true,
    ua_platform: true,
    ua_platform_version: true,
    bot: true
};
// Other parameters specific to mkt-srv
const CUSTOM_PARAMS = {
    page: true,
    path: true,
    unglobalized_path: true,
    category: true,
    label: true,
    description: true,
    destination_url: true,
    logged_in: true,
    site_locale: true,
    user_country: true,
    document_height: true,
    has_logged_in_last_six_months: true,
    scroll_y: true,
    ttfb: true,
    fps: true
};
export function getLayoutDimensions() {
    // Maximum of these two values is used for compatibility reasons
    const totalPageHeight = document.documentElement.scrollHeight;
    const bodyHeight = document.body.scrollHeight;
    return {
        document_height: Math.max(totalPageHeight, bodyHeight),
        scroll_y: window.scrollY,
        viewport_width: window.parent.innerWidth,
        viewport_height: window.parent.innerHeight
    };
}
export function getSiteAuthParams() {
    const params = {};
    if (cookies.get('site-auth') === '1') {
        params.logged_in = true;
    }
    if (cookies.get('__Secure-has_logged_in')) {
        params.has_logged_in_last_six_months = true;
    }
    return params;
}
export function getExperimentTokenParams() {
    const meta = document.querySelector('meta[name="experiment-token"]');
    const token = meta === null || meta === void 0 ? void 0 : meta.content;
    return token ? {
        token_b057adf6: token
    } : {};
}
function getSearchParamSetFromUrl(url, desiredParams) {
    const foundParams = {};
    if (!url.includes('?')) return foundParams;
    // strip everything before ?
    const urlParams = new URLSearchParams(url.slice(url.indexOf('?')));
    desiredParams.forEach((param)=>{
        const value = urlParams.get(param);
        if (value) {
            foundParams[param] = value;
        }
    });
    return foundParams;
}
function getUtmParams(url) {
    return getSearchParamSetFromUrl(url, Object.keys(UTM_PARAMS));
}
function pathFromUrl(url) {
    const urlObj = createURL(url);
    return (urlObj === null || urlObj === void 0 ? void 0 : urlObj.pathname) || window.location.pathname;
}
export class StripeAnalyticsProvider {
    ready() {
        var _this = this;
        return _async_to_generator(function*() {
            return !!_this.analyticsReporter;
        })();
    }
    trackPage({ id, path, path_with_qs: pathWithQS, referrer }) {
        var _this = this;
        return _async_to_generator(function*() {
            _this.analyticsReporter.viewed(getCleanedId(id), _object_spread({
                path,
                referrer
            }, _this.getCommonEventParams(pathWithQS)));
        })();
    }
    trackEvent({ category, action, label, description, url, destination_url: destinationUrl }) {
        var _this = this;
        return _async_to_generator(function*() {
            const pageUrl = url || window.location.href;
            const path = pathFromUrl(pageUrl);
            _this.analyticsReporter.action(action, _object_spread({
                path,
                category,
                label
            }, description !== undefined && {
                description
            }, destinationUrl && {
                destination_url: getHostAndPath(destinationUrl)
            }, _this.getCommonEventParams(pageUrl)));
        })();
    }
    trackVitals(vitals) {
        var _this = this;
        return _async_to_generator(function*() {
            const metrics = yield captureWebVitals(vitals);
            const pageUrl = window.location.href;
            const path = pathFromUrl(pageUrl);
            _this.analyticsReporter.action('web-vitals', _object_spread({
                path
            }, metrics, _this.getCommonEventParams(pageUrl)));
        })();
    }
    setExperimentTreatment({ trackingMetadata: { path, referrer }, experimentId, arbId, bucketingStrategy }) {
        var _this = this;
        return _async_to_generator(function*() {
            // Do not report exposure event if query param override used
            if (bucketingStrategy === 'query_params') return;
            const commonParams = _this.getCommonEventParams(path);
            const dimensions = {
                'dimensions-viewport_width': commonParams.viewport_width,
                'dimensions-viewport_height': commonParams.viewport_height,
                'dimensions-logged_in': !!commonParams.logged_in,
                'dimensions-has_logged_in_last_six_months': !!commonParams.has_logged_in_last_six_months
            };
            // Note that `viewed_name` is automatically added by analytics.js to match the last sent value.
            _this.analyticsReporter.action('experiment.exposed', _object_spread({
                path,
                referrer,
                arb_id: arbId,
                experiment_retrieved: experimentId
            }, dimensions, commonParams));
        })();
    }
    getCommonEventParams(pageUrl) {
        const { siteLocale, siteLanguage, userGeoIpCountry, unglobalizedPath } = this.getLocaleInfo();
        const params = _object_spread({
            unglobalized_path: unglobalizedPath,
            user_country: userGeoIpCountry,
            site_locale: siteLocale,
            stripe_locale: `${siteLanguage}-${siteLocale}`
        }, getUtmParams(pageUrl), getLayoutDimensions(), getSiteAuthParams(), getExperimentTokenParams());
        return params;
    }
    constructor(environment, pageViewToken, getLocaleInfo, aelClientId, aelNamespace){
        _define_property(this, "getLocaleInfo", void 0);
        _define_property(this, "analyticsReporter", void 0);
        this.getLocaleInfo = getLocaleInfo;
        this.analyticsReporter = new AnalyticsReporter({
            clientId: aelClientId || 'mkt-srv',
            namespace: aelNamespace || 'wpp',
            sessionization: true,
            inProduction: isProduction(environment),
            botDetection: true,
            pageviewtoken: pageViewToken,
            safeParams: _object_spread({}, EXPERIMENTATION_PARAMS, CUSTOM_PARAMS, UTM_PARAMS, UA_PARAMS)
        });
    }
}

//# sourceMappingURL=StripeAnalytics.js.map